import React from 'react';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';

import { styles } from '../WorkingHours.styles';
import { WorkingDay, TimeSlotEditorState } from '../WorkingHours.types';
import { WorkingHoursEditorValidation } from '../workingHoursSelectors';
import { minutesToTime12h } from '../utils/slotLabelUtils';
import { MINUTES_PER_DAY, VIEW_MAX_MINUTES, WEEKDAY_COUNT } from '../workingHoursConstants';

type TimeEditorPopoverProps = {
  anchorEl: HTMLElement | null;
  editor: TimeSlotEditorState;
  validation: WorkingHoursEditorValidation | null;
  days: WorkingDay[];
  onUpdateDraft: (payload: {
    startMinutes?: number;
    endMinutes?: number;
    overnight?: boolean;
  }) => void;
  onSetApplyDays: (payload: { applyDays: string[] }) => void;
  onApply: () => void;
  onCancel: () => void;
  onDelete: () => void;
};

const timeOptions = (extended: boolean) => {
  const max = extended ? VIEW_MAX_MINUTES : MINUTES_PER_DAY;
  const options: { value: number; label: string }[] = [];
  for (let m = 0; m <= max; m += 15) {
    const label = m >= MINUTES_PER_DAY ? `${minutesToTime12h(m)} (+1d)` : minutesToTime12h(m);
    options.push({ value: m, label });
  }
  return options;
};

const formatDuration = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h ? `${h}h` : ''}${m ? ` ${m}m` : ''}`.trim() || '0m';
};

const TimeEditorPopover = ({
  anchorEl,
  editor,
  validation,
  days,
  onUpdateDraft,
  onSetApplyDays,
  onApply,
  onCancel,
  onDelete,
}: TimeEditorPopoverProps) => {
  if (!editor) return null;

  const startOptions = timeOptions(false);
  const endOptions = timeOptions(editor.overnight);
  const weekdayIds = days.slice(0, WEEKDAY_COUNT).map(d => d.id);

  const toggleApplyDay = (dayId: string) => {
    const next = editor.applyDays.includes(dayId)
      ? editor.applyDays.filter(id => id !== dayId)
      : [...editor.applyDays, dayId];
    onSetApplyDays({ applyDays: next });
  };

  const applyShortcut = (dayIds: string[]) => {
    onSetApplyDays({ applyDays: dayIds });
  };

  const conflictingDayNames = (validation?.conflictingDayIds ?? [])
    .map(id => days.find(d => d.id === id)?.name.slice(0, 3))
    .filter(Boolean);

  return (
    <Popover
      open
      anchorEl={anchorEl}
      onClose={onCancel}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      // Popover's root (a fixed, full-viewport div — not just its invisible backdrop)
      // otherwise sits over the whole page and swallows the mousedown on the slot bar's
      // own drag handles underneath it. Disable pointer events on that root and opt the
      // actual popup content (paper) back in; outside-click-to-cancel is already handled
      // by DayRow's ClickAwayListener, not by Popover's own backdrop click.
      slotProps={{
        root: { style: { pointerEvents: 'none' } },
        paper: { style: { pointerEvents: 'auto' } },
      }}>
      {/* The popover is a JSX child of the slot bar, so React bubbles its clicks up through
          the bar's onClick (which opens/resets the editor) even though it's portaled outside
          the bar's DOM subtree. Stop propagation here so interacting with the popover never
          re-triggers the bar's click handler. */}
      <Box sx={styles.editorPopover} onClick={e => e.stopPropagation()}>
        <Box sx={styles.editorRow}>
          <Box sx={styles.editorField}>
            <Box sx={styles.editorLabel}>Start</Box>
            <Select
              size="small"
              value={editor.startMinutes}
              onChange={e => onUpdateDraft({ startMinutes: Number(e.target.value) })}
              MenuProps={{ disablePortal: true }}
              sx={styles.editorSelect}>
              {startOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={styles.editorField}>
            <Box sx={styles.editorLabel}>End</Box>
            <Select
              size="small"
              value={editor.endMinutes}
              onChange={e => onUpdateDraft({ endMinutes: Number(e.target.value) })}
              MenuProps={{ disablePortal: true }}
              sx={styles.editorSelect}>
              {endOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        <Box sx={styles.editorOvernightRow}>
          <Checkbox
            size="small"
            checked={editor.overnight}
            onChange={e => onUpdateDraft({ overnight: e.target.checked })}
          />
          Ends next day (overnight) 🌙
        </Box>

        {validation && <Box sx={styles.editorDuration}>Duration: {formatDuration(validation.duration)}</Box>}

        <Box sx={styles.editorApplyDaysLabel}>Apply to days</Box>
        <Box sx={styles.editorDayChips}>
          {days.map(day => {
            const isOn = editor.applyDays.includes(day.id);
            const hasConflict = validation?.conflictingDayIds.includes(day.id);
            return (
              <Box
                key={day.id}
                component="button"
                sx={styles.editorDayChip}
                className={`${isOn ? 'on' : ''} ${hasConflict ? 'conflict' : ''}`.trim()}
                onClick={() => toggleApplyDay(day.id)}>
                {day.name.slice(0, 3)}
              </Box>
            );
          })}
          <Box
            component="button"
            sx={styles.editorDayChipShortcut}
            onClick={() => applyShortcut(weekdayIds)}>
            Weekdays
          </Box>
          <Box
            component="button"
            sx={styles.editorDayChipShortcut}
            onClick={() => applyShortcut(days.map(d => d.id))}>
            All
          </Box>
        </Box>

        {validation?.conflictMessage && (
          <Box sx={styles.editorWarning}>{validation.conflictMessage}</Box>
        )}
        {!validation?.conflictMessage && conflictingDayNames.length > 0 && (
          <Box sx={styles.editorWarningAmber}>
            Overlaps an existing time frame on: {conflictingDayNames.join(', ')}. Resolve or
            deselect these days to apply.
          </Box>
        )}

        <Box sx={styles.editorActions}>
          <Button size="small" onClick={onDelete} sx={styles.editorDeleteButton}>
            Delete
          </Button>
          <Button size="small" variant="outlined" onClick={onCancel} sx={styles.cancelButton}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={onApply}
            disabled={!!validation?.conflictMessage || conflictingDayNames.length > 0}
            sx={styles.saveButton}>
            Apply
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default TimeEditorPopover;
